import React, { useEffect, useMemo, useState } from "react";
import { useAuth } from "../../auth";
import { getClientByEmail } from "../../services/clientService";
import { useNavigate } from "react-router-dom";
import {
  downloadOrderResults,
  getAllCompletedOrdersByClientID,
} from "../../services/orderService";
import { getNotifications } from "../../services/notificationsService";
import { ClientNotification } from "../../interfaces/clientNotification";
import NotificationsPopup from "../../components/client-components/NotificationsPopup";
import { Client } from "../../interfaces/client";
import ToolTip from "../../components/component-tools/ToolTip";
import { SimpleOrder } from "../../interfaces/simpleOrder";

const PastCandidates = () => {
  const [client, setClient] = useState<Client | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const { decodeToken } = useAuth();
  const [orders, setOrders] = useState<SimpleOrder[]>();
  const [showFilter, setShowFilter] = useState(false);
  const [startDate, setStartDate] = useState<string | null>(null);
  const [endDate, setEndDate] = useState<string | null>(null);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [loadingSpinner, setLoadingSpinner] = useState(false);
  const [notifications, setNotifications] = useState<ClientNotification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false);

  type SortMode = "updated" | "name";
  const [sortMode, setSortMode] = useState<SortMode>("updated");
  const [sortDisabled, setSortDisabled] = useState(true);

  useEffect(() => {
    const loadClientandOrders = async () => {
      try {
        setSortDisabled(true);
        const email = decodeToken(localStorage.getItem("token") || "");
        const clientIDString = localStorage.getItem("clientID");
        const clientID = clientIDString ? parseInt(clientIDString, 10) : 0;
        const client = await getClientByEmail(email, clientID);
        setClient(client);

        const ordersResponse = await getAllCompletedOrdersByClientID(
          client.clientID,
        );
        setOrders(ordersResponse);

        const notificationsResponse = await getNotifications(client?.clientID);
        notificationsResponse.sort(
          (a, b) =>
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
        );
        setNotifications(notificationsResponse);

        const unreadCount = notificationsResponse.length;
        setUnreadCount(unreadCount);
        setSortDisabled(false);
      } catch (error) {
        console.error("Error fetching client:", error);
      } finally {
        setLoading(false);
      }
    };

    loadClientandOrders();
  }, [decodeToken]);

  const filteredOrders = useMemo(() => {
    if (!orders) return [];

    const term = searchTerm.trim().toLowerCase();

    const toTime = (d?: string | Date | null) =>
      d ? (d instanceof Date ? d.getTime() : Date.parse(d)) : 0;

    const filtered = orders.filter((order) => {
      const matchesName =
        `${order.candidateFirstName} ${order.candidateLastName}`
          .toLowerCase()
          .includes(term);

      const completed = order.completedDate
        ? new Date(order.completedDate)
        : null;

      const inStart =
        !startDate || !completed || completed >= new Date(startDate);
      const inEnd = !endDate || !completed || completed <= new Date(endDate);

      return matchesName && inStart && inEnd;
    });

    const byUpdated = (a: SimpleOrder, b: SimpleOrder) =>
      toTime(b.completedDate) - toTime(a.completedDate); // newest first

    const byName = (a: SimpleOrder, b: SimpleOrder) =>
      `${a.candidateLastName} ${a.candidateFirstName}`.localeCompare(
        `${b.candidateLastName} ${b.candidateFirstName}`,
        undefined,
        { sensitivity: "base" },
      );

    return [...filtered].sort(sortMode === "updated" ? byUpdated : byName);
  }, [orders, searchTerm, startDate, endDate, sortMode]);

  const applyFilter = () => {
    setShowFilter(false);
  };

  const resetFilter = () => {
    setStartDate(null);
    setEndDate(null);
    setShowFilter(false);
  };

  const handleDownloadResults = async () => {
    if (filteredOrders == null) return;
    setLoadingSpinner(true);

    try {
      const pdfBlob = await downloadOrderResults(filteredOrders);
      if (pdfBlob) {
        const csvUrl = URL.createObjectURL(pdfBlob);
        const link = document.createElement("a");
        link.href = csvUrl;
        link.download = "order_results.csv";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        console.error("Error creating report.");
        return;
      }
    } catch {
    } finally {
      setLoadingSpinner(false);
    }
  };

  const resetAndApplySort = (newSort: SortMode) => {
    setSortMode(newSort);
  };

  return (
    <div className="content-container">
      <div className="header-container">
        <h1 style={{ display: "flex" }}>
          Completed Candidates{" "}
          <ToolTip
            tooltipText={`Blue: Criminal\nOrange: Reference`}
            placement="right"
            className="info-icon"
          />
        </h1>
        <div className="page-items-container">
          <div className="sort-toggle">
            <button
              className={`btn-funnel ${sortDisabled ? "disabled" : ""}`}
              onClick={() =>
                resetAndApplySort(sortMode === "updated" ? "name" : "updated")
              }
              title={
                sortMode === "updated"
                  ? "Sorted by Completed Date"
                  : "Sorted by Last Name"
              }
              disabled={sortDisabled}
            >
              {sortMode === "updated" ? (
                <i className="bi bi-clock-history filter-icon"></i>
              ) : (
                <i className="bi bi-sort-alpha-down filter-icon"></i>
              )}
            </button>
          </div>
          <div className="sort-toggle">
            <button
              className={`btn-funnel`}
              onClick={() => handleDownloadResults()}
              title={"Download Results"}
            >
              <i className="bi bi-file-earmark-arrow-down filter-icon"></i>
            </button>
          </div>
          <button
            className="btn-funnel"
            onClick={() => setShowFilter(!showFilter)}
          >
            <i className="bi bi-funnel-fill filter-icon"></i>
          </button>
          <div className="search-container">
            <input
              style={{ border: "none", width: "100%", outline: "none" }}
              type="text"
              placeholder="Search Candidate Name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <i className="bi bi-search search-icon"></i>
          </div>
          <button
            className="notification-button"
            onClick={() => setShowNotifications(true)}
          >
            <i className="bi bi-bell-fill"></i>
            {unreadCount > 0 && (
              <span className="notification-dot">{unreadCount}</span>
            )}
          </button>
        </div>
        {showNotifications && client?.clientID && (
          <NotificationsPopup
            notificationsInput={notifications}
            clientID={client?.clientID}
            onClose={(
              newUnreadCount = unreadCount,
              newNotifications = notifications,
            ) => {
              setNotifications(newNotifications);
              setShowNotifications(false);
              setUnreadCount(newUnreadCount);
            }}
          />
        )}
      </div>

      {/* Filter Popup */}
      {showFilter && (
        <>
          <div
            className="background-mask"
            onClick={() => setShowFilter(false)}
          ></div>
          <div className="filter-popup">
            <div className="close-button-container">
              <button
                className="close-button"
                onClick={() => setShowFilter(false)}
              >
                <i className="bi bi-x-circle"></i>
              </button>
            </div>
            <div className="filter-header">
              <h2>Filter Completed Candidates</h2>
              <div>Filter on completed date.</div>
            </div>
            <div className="filter-body">
              <div className="field-stacked">
                <label className="field-label">Date Range:</label>
                <div className="date-range-inputs">
                  <input
                    className="form-control"
                    type="date"
                    value={startDate || ""}
                    onChange={(e) => setStartDate(e.target.value)}
                  />
                  <span>to</span>
                  <input
                    className="form-control"
                    type="date"
                    value={endDate || ""}
                    onChange={(e) => setEndDate(e.target.value)}
                  />
                </div>
              </div>
            </div>
            <div className="filter-actions">
              <button className="smbtn btn-grey" onClick={resetFilter}>
                Reset
              </button>
              <button className="smbtn btn-green" onClick={applyFilter}>
                Apply
              </button>
            </div>
          </div>
        </>
      )}

      {loading ? (
        <div className="loading-divider">
          <div className="loading-bar"></div>
        </div>
      ) : (
        <>
          <div className="divider"></div>

          <div className="table-wrapper">
            <div className="table-container">
              <table className="table" style={{ cursor: "pointer" }}>
                <thead>
                  <tr style={{ cursor: "default" }}>
                    <th style={{ width: "200px" }}>Candidate Name</th>
                    <th style={{ width: "300px" }}>Designation</th>
                    <th style={{ width: "150px" }}></th>
                    <th style={{ width: "50px" }}>Type</th>
                    <th style={{ width: "120px" }}>Initiated Date</th>
                    <th style={{ width: "120px" }}>Completed Date</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredOrders?.length === 0 ? (
                    <tr>
                      <td colSpan={6} style={{ textAlign: "center" }}>
                        No candidates found.
                      </td>
                    </tr>
                  ) : (
                    filteredOrders?.map((order) => (
                      <tr
                        key={order.orderID}
                        onClick={() =>
                          navigate(`/client/view-order/${order.orderID}`)
                        }
                      >
                        <td>
                          <u>
                            {(order.applican_link || order.candidat_link) &&
                              "*"}
                            {order.candidateFirstName} {order.candidateLastName}
                          </u>
                        </td>
                        <td>{order.designation}</td>
                        <td>
                          {order.orderStatusID === 6 && (
                            <div
                              className={`chip-date status-subscription`}
                              style={{ gap: "10px", width: "150px" }}
                            >
                              <div>Subscription</div>
                            </div>
                          )}
                        </td>
                        <td>
                          <div className="order-status-indicator">
                            <div
                              className={`circle-status 
      ${order.hasReferenceOrder ? "has-reference" : "no-reference"} 
      ${order.referenceInAdminReview ? "reference-active" : ""} 
      ${order.hasCriminalOrder ? "has-criminal" : "no-criminal"} 
      ${order.criminalInAdminReview ? "criminal-active" : ""}`}
                            ></div>
                          </div>
                        </td>
                        <td>{order.initiatedDate?.toLocaleDateString()}</td>
                        <td>
                          {order.completedDate?.toLocaleDateString() ||
                            "Partial"}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
      {loadingSpinner && (
        <div className="loading-mask">
          <div className="loading-spinner"></div>
        </div>
      )}
    </div>
  );
};

export default PastCandidates;
